import { Collapsible, Text, HStack, Input, Separator, Center, Flex, Link } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import * as gqlTypes from "@hydrologiq/gql/graphql"

import { useEffect, useState } from "react"
import { useMutation } from "@apollo/client"
import Form, { FormActionOverrideProps } from "@hydrologiq/shared/chakra/custom/Form"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import Map, { LocationMarker } from "./map/Map"
import {
  CollapseButton,
  DeleteButton,
  EditButton,
  AssignLocationButton,
} from "@hydrologiq/shared/content/components/Button"
import { DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS } from "@hydrologiq/shared/copy/constants"
import { useAppDispatch } from "@hydrologiq/shared/state/hooks"
import { addLocation } from "@hydrologiq/shared/state/slices/locationsSlice"

type LocationFormData = {
  id?: string
  name?: string
  lat: number
  long: number
}
interface ManageLocationProps {
  location?: string
  locations?: gqlTypes.Location[]
  onUpdate?: (location?: string) => void
  mapsApiKey?: string
  newLocationDefaults?: LocationFormData
}

const formatName = (data: LocationFormData) => {
  if (data.name) return data.name
  if (data.lat && data.long) return `${data.long},${data.lat}`
  return "No location specified"
}

const ManageLocation = (props: ManageLocationProps) => {
  const {
    location,
    locations,
    mapsApiKey,
    onUpdate,
    newLocationDefaults = {
      name: "No location specified",
      lat: 54.9753147,
      long: -2.269908,
    },
  } = props
  const dispatch = useAppDispatch()
  const [formData, setFormData] = useState<LocationFormData>(newLocationDefaults)

  const [collapsed, setCollapsed] = useState(true)
  const [createNewLocation, { loading: createNewLocationLoading }] = useMutation(gqlTypes.CreateLocationDocument)

  const [selectedLocation, setSelectedLocation] = useState(location)
  const [locationId, setLocationId] = useState(location)
  const [newLocation, setNewLocation] = useState(locationId === undefined)

  const loading = createNewLocationLoading

  const [newLocationMarker, setNewLocationMarker] = useState<LocationMarker | undefined>()

  const [currentLocation, setCurrentLocation] = useState<gqlTypes.Location | undefined | null>()
  useEffect(() => {
    setCurrentLocation(
      newLocationMarker !== undefined
        ? ({
            id: "new",
            lat: newLocationMarker.location.lat,
            long: newLocationMarker.location.long,
            name: formData.name,
          } as gqlTypes.Location)
        : locationId !== undefined
        ? locations?.find((l) => l.id === locationId)
        : undefined
    )
  }, [newLocationMarker, newLocation, locationId, locations, location])
  useEffect(() => {
    if (newLocation) {
      setFormData({
        name: newLocationDefaults.name,
        lat: newLocationDefaults.lat,
        long: newLocationDefaults.long,
      })
    }
  }, [newLocation])
  useEffect(() => {
    if (currentLocation) {
      setFormData((prev) => ({
        ...prev,
        lat: currentLocation.lat,
        long: currentLocation.long,
      }))
    }
  }, [currentLocation])
  useEffect(() => {
    if (selectedLocation) {
      setLocationId(selectedLocation)
    }
  }, [selectedLocation])

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
  })
  const selectedLocationItem = locations?.find((l) => l.id === selectedLocation) ?? undefined
  const locationSelected = selectedLocation && selectedLocationItem

  const switchDisabled = loading || (newLocation && locations && locations.length === 0)
  return (
    locations && (
      <Collapsible.Root
        pt={3}
        width={"100%"}
        open={!collapsed}
        onOpenChange={(e) => !locationSelected && setCollapsed(!e.open)}
      >
        <HStack justifyContent={"space-between"} width={"100%"}>
          <HStack justifyContent={"space-between"} width={"100%"} alignItems={"flex-end"}>
            <Field label="Location">
              <Text>
                {collapsed &&
                  (locationSelected
                    ? selectedLocationItem.name || formatName(selectedLocationItem as LocationFormData)
                    : "No location specified")}
              </Text>
            </Field>
            &nbsp;
            <Flex>
              {locationSelected && collapsed && (
                <DeleteButton
                  size="xs"
                  onClick={() => {
                    setLocationId(undefined)
                    setNewLocation(true)
                    setNewLocationMarker({
                      location: { lat: newLocationDefaults.lat, long: newLocationDefaults.long },
                      title: formatName(newLocationDefaults),
                    })
                    setSelectedLocation(undefined)
                    onUpdate?.(undefined)
                  }}
                  disabled={locationId === undefined}
                />
              )}
              {!locationSelected &&
                (!collapsed ? (
                  <CollapseButton onClick={() => setCollapsed(true)} />
                ) : (
                  <EditButton onClick={() => setCollapsed(false)} />
                ))}
            </Flex>
          </HStack>
        </HStack>

        <Collapsible.Content mt={2} p={4} pt={5} outline="1px solid #d9d9d9" borderRadius="5px">
          <Form
            helperText={
              <Flex fontSize="13px !important">
                <Text>
                  {newLocation ? "Please create a new location below or" : "Please select an existing location or"}
                </Text>
                <Text>&nbsp;</Text>
                <Link
                  disabled={switchDisabled}
                  variant="primary"
                  onClick={(e) => {
                    e.preventDefault()
                    if (switchDisabled) return
                    if (newLocation) {
                      setNewLocation(false)
                      const historyLocationId = location || locations.at(0)?.id
                      const historyLocation = locations.find((l) => l.id === historyLocationId)
                      if (historyLocation) {
                        setFormData((prev) => ({
                          ...prev,
                          name: historyLocation.name || formatName(historyLocation as LocationFormData),
                          lat: historyLocation.lat,
                          long: historyLocation.long,
                        }))
                      }
                      setLocationId(historyLocationId)
                      setNewLocationMarker(undefined)
                    } else {
                      setNewLocation(true)
                      setNewLocationMarker({
                        location: { lat: newLocationDefaults.lat, long: newLocationDefaults.long },
                        title: formatName(newLocationDefaults),
                      })
                    }
                  }}
                >
                  {newLocation ? "select an existing location" : "create a new location"}
                </Link>
              </Flex>
            }
            actionType="change"
            loading={loading}
            onSubmit={async () => {
              let location = locationId
              if (newLocation) {
                if (formData.name) {
                  const { data } = await createNewLocation({
                    variables: {
                      name: formData.name,
                      long: formData.long,
                      lat: formData.lat,
                    },
                  })
                  if (data?.createLocation) {
                    location = data.createLocation.id
                    setNewLocation(false)
                    dispatch(addLocation(data.createLocation))
                  }
                }
              }
              setSelectedLocation(location)
              onUpdate?.(location)
              setCollapsed(true)
              return true
            }}
            onCancel={() => setCollapsed(true)}
            canSubmit={formData.name !== undefined && formData.name.length > 0}
            layout="column"
            ActionOverride={(props: FormActionOverrideProps) => {
              const { canSubmit, onSubmit } = props
              return (
                <Center>
                  <AssignLocationButton
                    type="submit"
                    value="primary"
                    disabled={!canSubmit}
                    size="lg"
                    description={`Assign the current location to the ${copy[
                      COPY_KEYS.HEADINGS.SITE
                    ].toLocaleLowerCase()}`}
                    onClick={async (e) => {
                      e.preventDefault()
                      if (canSubmit) await onSubmit()
                    }}
                  />
                </Center>
              )
            }}
            outsideStack={
              <Collapsible.Root open={true}>
                <Collapsible.Content pb={3}>
                  <HStack>
                    <Field label="Longitude" pt={3} required={locationId === undefined} disabled={!newLocation}>
                      <Input
                        name="longitude"
                        type="number"
                        step={0.000001}
                        value={currentLocation?.long}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value)
                          value && currentLocation && setCurrentLocation({ ...currentLocation, long: value })
                        }}
                      />
                    </Field>
                    <Field label="Latitude" pt={3} required={locationId === undefined} disabled={!newLocation}>
                      <Input
                        name="latitude"
                        type="number"
                        step={0.000001}
                        value={currentLocation?.lat}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value)
                          value && currentLocation && setCurrentLocation({ ...currentLocation, lat: value })
                        }}
                      />
                    </Field>
                  </HStack>
                </Collapsible.Content>
              </Collapsible.Root>
            }
          >
            <>
              {!newLocation ? (
                <NativeSelectRoot>
                  <NativeSelectField
                    onChange={(e) => {
                      const location = locations.find((l) => l.id === e.target.value)
                      if (location) {
                        setFormData((prev) => ({
                          ...prev,
                          name: location.name || `${location.lat},${location.long}`,
                          lat: location.lat,
                          long: location.long,
                        }))
                      }
                      setLocationId(e.target.value as string)
                      setNewLocation(false)
                    }}
                    key={locations.length}
                    value={locationId}
                    name="locationId"
                    items={[
                      ...locations
                        .map((value) => ({
                          label: formatName({ name: value.name ?? undefined, lat: value.lat, long: value.long }),
                          value: value.id,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label)),
                    ]}
                  />
                </NativeSelectRoot>
              ) : (
                <Input
                  name="name"
                  minHeight="40px"
                  placeholder="Enter location name..."
                  defaultValue={props.newLocationDefaults?.name}
                  onChange={(e) => {
                    const name = e.target.value
                    setFormData((prev) => ({
                      ...prev,
                      name: name.length > 0 ? name : undefined,
                    }))
                  }}
                />
              )}
            </>
          </Form>
          {mapsApiKey && (
            <Map
              focusMarker={
                currentLocation ? { location: currentLocation, title: currentLocation.name || "Location" } : undefined
              }
              locationChange={(loc) => {
                if (newLocation) setNewLocationMarker({ location: loc, title: formData.name || "New Location" })
              }}
              zoom={8}
              apiKey={mapsApiKey}
              width="100%"
            />
          )}
        </Collapsible.Content>
      </Collapsible.Root>
    )
  )
}

export default ManageLocation
